// Unpublished Work © 2022-2026 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {connect} from 'react-redux';
import {withRouter} from 'react-router-dom';
import {DatePicker} from '@deere/isg.component-library';
import {IconEdit} from '@deere/icons';
import MediaQuery, {MOBILE_MEDIA_QUERY} from 'Ui/components/higher-order-components/media-query';
import {getHoc} from 'Services/hoc-service';
import {fetchEffectData} from 'Utils/react-utils';
import {getHocValue, convertToFormattedUoM} from 'Utils/unit-conversion-utils';
import {openDialog} from 'Store/actions/dialogs';
import dialogTypes from 'Ui/components/common/dialog-types';
import {COURSE} from 'Common/constants/membership-type';
import {HOC as HOC_CONSTANT, HOC_SPEC as HOC_SPEC_CONSTANT} from 'Common/constants/data-group-constants';
import {INCHES} from 'Common/constants/data-unit-constants';
import {sortBy} from 'lodash';
import moment from 'moment';
import {useNavBarActions} from 'Ui/react-hooks/use-navbar-actions';
import {MANAGE_EQUIPMENT_SETTINGS} from 'Common/constants/business-activities';
import dayjs from 'dayjs';
import DataGridWrapper from 'Src/ui-core/components/common/data-table/data-grid-wrapper';

function getDataGridColumns(translations) {
    return [
        {
            headerName: translations.AREA,
            flex: 1,
            field: 'name'
        },
        {
            headerName: translations.ONLINK_HOC,
            flex: 1,
            align: 'right',
            headerAlign: 'right',
            field: 'hocValue'
        },
        {
            headerName: translations.ONLINK_SPECIAL,
            flex: 1,
            align: 'right',
            headerAlign: 'right',
            field: 'hocSpecialValue'
        }
    ];
}

async function openHocEditDialog(date, openDialog, membership, updateCourseMembership, translations, dataGroup) {
    openDialog({
        initialDate: date,
        membership,
        translations,
        updateCourseMembership,
        dataGroup
    });
}

function getFilterComponent(date, setDate, openDialog, translations, membership, updateCourseMembership, membershipIsACourse) {
    const editHocDialog = () => openHocEditDialog(date, openDialog, membership, updateCourseMembership, translations, HOC_CONSTANT);
    const editSpecialHocDialog = () => openHocEditDialog(date, openDialog, membership, updateCourseMembership, translations, HOC_SPEC_CONSTANT);

    const itemsNewNav = membershipIsACourse ? [
        {
            title: 'EDIT',
            onClick: editHocDialog,
            variant: 'primary',
            Icon: <IconEdit/>,
            requiredMyJdPermissions: MANAGE_EQUIPMENT_SETTINGS
        },
        {
            title: 'ONLINK_EDIT_SPECIAL',
            onClick: editSpecialHocDialog,
            Icon: <IconEdit/>,
            requiredMyJdPermissions: MANAGE_EQUIPMENT_SETTINGS
        }
    ] : [];

    useNavBarActions(itemsNewNav);

    return (
        <div className='hoc-extra-table-options extra-table-options'>
            <div className={'hoc'}>
                <DatePicker
                    onChange={(date) => setDate(dayjs(date))}
                    value={date.toDate()}
                />
            </div>
        </div>
    );
}

function initializeState() {
    const [date, setDate] = React.useState(() => moment());
    const [hoc, setHoc] = React.useState(() => []);
    const [loading, setLoading] = React.useState(() => true);

    return {
        date,
        setDate,
        hoc,
        setHoc,
        loading,
        setLoading
    };
}

function getHocBasedOnMembershipType(hoc, hocSpecial, translations, membershipIsACourse, unitOfMeasure) {
    if (membershipIsACourse) {
        return {
            hocValue: getHocValue(hoc, translations),
            hocSpecialValue: getHocValue(hocSpecial, translations)
        };
    }

    const sharedConversionConfig = {
        dataUnit: INCHES,
        translations,
        unitOfMeasure
    };

    return {
        hocValue: convertToFormattedUoM(hoc?.hocValue, sharedConversionConfig),
        hocSpecialValue: convertToFormattedUoM(hocSpecial?.hocValue, sharedConversionConfig)
    };
}

function getFormattedHOCData(equipmentAreas, translations, membershipIsACourse, unitOfMeasure) {
    const hocs = equipmentAreas.filter((hoc) => !hoc.name.includes('Special'));
    const hocsSpecial = equipmentAreas.filter((hoc) => hoc.name.includes('Special'));

    const hocData = hocs.map((hoc) => {
        const hocSpecial = hocsSpecial.find((hocSpec) => hocSpec.name === `${hoc.name} Special`);

        const {
            hocValue, hocSpecialValue
        } = getHocBasedOnMembershipType(hoc, hocSpecial, translations, membershipIsACourse, unitOfMeasure);

        return {
            ...hoc,
            name: translations[`HOC ${hoc.name}`],
            hocValue,
            hocSpecialValue
        };
    });

    const filterHocData = hocData.filter((hoc) => hoc.hocValue || hoc.hocSpecialValue);

    return sortBy(filterHocData, 'seq');
}

function HOC(props) {
    const {
        membership,
        openDialog,
        translations
    } = props;

    const {
        date,
        setDate,
        hoc,
        setHoc,
        loading,
        setLoading
    } = initializeState();

    const membershipIsACourse = membership.membershipType === COURSE;

    const updateHocData = () => fetchEffectData(async (isMounted) => {
        setLoading(true);

        const formattedDate = date.format('YYYYMMDD');

        const {equipmentAreas} = await getHoc(formattedDate);

        const hocData = getFormattedHOCData(equipmentAreas, translations, membershipIsACourse, membership.unitOfMeasure);

        if (isMounted()) {
            setHoc(hocData);
            setLoading(false);
        }
    });

    const updateCourseMembership = ({dateSelected}) => {
        if (dateSelected.isSame(date, 'day')) {
            updateHocData();
        }
    };

    React.useEffect(updateHocData, [membership, date]);

    return (<DataGridWrapper
        columns={getDataGridColumns(translations)}
        filterComponent={getFilterComponent(date, setDate, openDialog, translations, membership, updateCourseMembership, membershipIsACourse)}
        getRowId={(row) => row.equipmentAreaId}
        loading={loading}
        noDataAvailableMessage={translations.ONLINK_NO_HOCS_FOR_DATE_SELECTED}
        rows={hoc}
        slotProps={{
            root: {
                disableColumnMenu: true
            }
        }}
        translations={translations}
    />);
}

HOC.propTypes = {
    membership: PropTypes.object,
    openDialog: PropTypes.func,
    translations: PropTypes.translations
};

export function mapStateToProps(state) {
    return {
        membership: state.membership
    };
}

export function mapDispatchToProps(dispatch) {
    return {
        openDialog: (props) => {
            dispatch(openDialog(dialogTypes.HOC_EDIT_DIALOG, props));
        }
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(MediaQuery(MOBILE_MEDIA_QUERY)(withRouter(HOC)));
